/**
 * Meeple placement UX. [P6-meeple-ux]
 *
 * After a tile is placed, `legalMeepleTargets` (computed locally, never re-derived by the client) tells
 * us exactly which features on that tile can still take a meeple. Each becomes a clickable DOM chip
 * projected over its position — accessible and touch-friendly by construction — plus a colour-matched,
 * pulsing ring in the 3D scene so it's clear *where* each option actually is. Skipping is one click.
 *
 * Iteration 1 of the meeple-ux lane brief (`.planning/lane-meeple-ux.md`) added: screen-space collision
 * resolution so chips never overlap and become unclickable at shallow camera angles, a leader line back
 * to each chip's ring once it's been displaced, per-target colour coding (ring + chip border/dot), a
 * generous invisible hit-area so the ring itself is clickable (not just the chip), and two-way hover/
 * focus linkage between a chip and its ring.
 */

import {
  CircleGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  Raycaster,
  RingGeometry,
  Vector2,
  Vector3,
} from 'three';
import {
  completedValue,
  endValue,
  describeFeature,
  find,
  nodeId,
  occupantsOf,
  posKey,
  totalSupply,
  type FeatureKind,
  type GameState,
  type Pos,
} from '@carc/engine';
import type { Scene } from './scene.js';
import type { BoardView } from './board-view.js';
import type { PlayableSession } from './session.js';
import type { SoundBoard } from './audio.js';
import { featureAnchor } from './geometry.js';
import { TILE_HEIGHT, TILE_SIZE } from './tile-mesh.js';

interface ScreenPoint {
  x: number;
  y: number;
}

interface ChipEntry {
  readonly featureIndex: number;
  /** False for a feature shown only to explain why it cannot be taken. */
  readonly available: boolean;
  readonly chip: HTMLButtonElement;
  readonly ring: Group;
  readonly ringMesh: Mesh<RingGeometry, MeshBasicMaterial>;
  readonly hitMesh: Mesh<CircleGeometry, MeshBasicMaterial>;
  readonly line: SVGLineElement;
  /** Drawn under `line` so a pale leader stays legible over pale artwork. */
  readonly lineShadow: SVGLineElement;
  /**
   * The anchor in the TILE's own local space, not the world's.
   *
   * It used to be captured as a world position once, at activate() — and the tile is still animating
   * into place at that moment (and its world matrix has not been refreshed since it was added), so the
   * leader lines pointed at where the tile briefly was rather than where its rings are. Kept local and
   * transformed every frame, it is right no matter what the tile or the camera does.
   */
  readonly localAnchor: Vector3;
  readonly baseOpacity: number;
  screenAnchor: ScreenPoint;
  chipPos: ScreenPoint;
  chipSize: { w: number; h: number };
  pulseFade: number;
}

const KIND_LABEL: Record<FeatureKind, string> = {
  road: 'Road',
  city: 'City',
  field: 'Field',
  cloister: 'Cloister',
};

// Okabe–Ito colour-blind-safe qualitative palette (minus black, which reads poorly on the dark board
// backdrop). One target = one colour; cycles if a tile somehow offers more targets than colours.
const TARGET_PALETTE = ['#56b4e9', '#e69f00', '#009e73', '#cc79a7', '#f0e442', '#0072b2'];

/** Deliberately outside the palette: a claimed feature must never read as one of the choices. */
const TAKEN_COLOUR = '#8b8b8b';

const CLICK_SLOP_PX = 10;

/**
 * Chips are laid out in a LANE beside the played tile, never on top of it. [P6-chip-lane]
 *
 * They used to sit at each feature's own projected point and only shuffle downward when they collided
 * with one another. That is fine when the board is close, and it collapses when it is not: zoom out and
 * every anchor on a tile projects to nearly the same pixel, so the whole set stacks into a column
 * centred on the very tile you are choosing on. Tony's screenshot has six chips in a vertical stripe
 * over the board, and he could neither see the tile nor tell which chip meant which feature. He had to
 * rotate and zoom the camera to dig his way out, which is not a thing a player should have to do.
 *
 * So the tile's own screen box is computed each frame and the chips are stacked clear of it, on
 * whichever side has more free room, ordered top-to-bottom by where their rings actually are. The
 * leader lines then earn their keep — they are long, they cross open ground, and each one physically
 * connects a chip to its ring. ([D-37])
 */
const LANE_GAP_PX = 18;
const CHIP_MIN_GAP_PX = 4;

/**
 * How long the chips refuse input after they appear. [P6-mobile-picker]
 *
 * On a touch screen the browser follows a tap's `pointerup` with a synthesized compatibility mouse
 * sequence — `mousedown`, `mouseup`, `click` — hit-tested against the DOM **as it stands when they are
 * dispatched**, not as it stood when the finger went down. The chips are built synchronously inside
 * that same `pointerup` (the tile is committed, `advance()` runs, `activate()` runs), so by the time
 * the ghost `click` is dispatched a chip is already sitting under the player's finger — over the tile
 * they just placed, which is precisely where they tapped. The chip takes the click and the meeple is
 * claimed before anyone has read a word.
 *
 * Measured on an emulated iPhone in landscape, 11 turns of ordinary tap-to-place: 7 turns had a ghost
 * click land on a chip and 5 turns lost the choice outright. The two that survived had landed on a
 * greyed "already claimed" chip, which `choose()` refuses — which is exactly why Tony saw the greyed
 * chips appear later in a game while the real choice never did.
 *
 * 400ms is longer than any browser's tap-delay (the historical worst case is ~350ms) and far shorter
 * than reading four chips, so it costs a real player nothing.
 */
const ARM_DELAY_MS = 400;
const CHIP_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 10;
const PULSE_HZ = 1.6;
const HIT_RADIUS_SCALE = 0.22; // vs the visible ring's outer radius of 0.13 — a generous tap target

const clamp = (v: number, lo: number, hi: number): number => (lo > hi ? (lo + hi) / 2 : Math.min(Math.max(v, lo), hi));

/**
 * What a feature is worth, stated honestly.
 *
 * The earlier version showed the CLOSED value and labelled it "so far", which is misleading in exactly
 * the way that loses you a game: an open two-tile city with a pennant reads as 6 points when, left
 * open, it is worth 3. Cities and roads pay full rate only when closed and half rate at game end, so
 * the chip now shows both numbers and never implies you already have the bigger one.
 */
function labelFor(kind: FeatureKind, closedPoints: number, openPoints: number, complete: boolean): string {
  if (kind === 'field') return 'Farm · 3 pts for each finished city it touches, at game end';
  if (complete) return `${KIND_LABEL[kind]} · closes now for ${closedPoints} pts`;
  if (kind === 'cloister') return `Cloister · ${closedPoints} pts if finished · ${openPoints} as it stands`;
  return `${KIND_LABEL[kind]} · ${closedPoints} pts if you close it · ${openPoints} if it stays open`;
}

/**
 * Why a feature on the just-placed tile cannot take a meeple, or `null` if it can.
 *
 * Silently omitting an unavailable feature is what made a correctly-occupied road look like an engine
 * bug ([D-27]): the owner saw two Farm chips, no Road chip, and reasonably concluded the road had been
 * forgotten — when in fact his tile had joined it to a road someone had already claimed a tile away,
 * possibly off the edge of what he could see. The rule is real and it is *invisible*, which is the
 * worst combination. So the feature is still shown, greyed, saying exactly who holds it.
 */
function unavailableReason(
  state: GameState,
  lastPlaced: Pos,
  featureIndex: number,
  seatName: (seat: number) => string,
): string | null {
  if (totalSupply(state.supply[state.activeSeat]) <= 0) return 'no meeples left';

  const root = find(state.forest, nodeId(lastPlaced, featureIndex));
  const occupants = occupantsOf(state.forest, state.meeples, root);
  if (occupants.length === 0) return null;

  const seats = [...new Set(occupants.map((m) => m.seat))];
  const who = seats.map(seatName).join(' & ');
  // Distinguish "you already have someone here" from "someone else does" — the first is a decision
  // you made, the second is news.
  const elsewhere = occupants.every((m) => posKey(m.pos) !== posKey(lastPlaced));
  return elsewhere ? `already claimed by ${who}, further along` : `already claimed by ${who}`;
}

export class MeeplePicker {
  private active = false;
  private entries: ChipEntry[] = [];
  private hoveredIndex: number | null = null;
  private pointerDown: ScreenPoint | null = null;
  /** Nothing may be chosen before this moment. See `ARM_DELAY_MS`. */
  private armedAt = 0;
  private armTimer = 0;
  private pulseClock = 0;
  private readonly reducedMotion: boolean;
  private readonly raycaster = new Raycaster();
  private readonly layer: HTMLElement;
  private readonly linesLayer: SVGSVGElement;
  private readonly topBarEl: HTMLElement | null;
  private readonly untick: () => void;
  /** Corners of the played tile in ITS OWN local space, plus the group they belong to. Transformed
   *  every frame for the same reason the anchors are — see `ChipEntry.localAnchor`. */
  private tileCorners: Vector3[] = [];
  private tileGroup: Group | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly board: BoardView,
    private readonly session: PlayableSession,
    private readonly sounds: SoundBoard,
    private readonly onResolved: () => void,
    /** Names an occupying seat, so a greyed chip can say who holds the feature. */
    private readonly seatName: (seat: number) => string = (seat) => `Player ${seat + 1}`,
  ) {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.layer = document.createElement('div');
    this.layer.className = 'meeple-picker-layer';
    Object.assign(this.layer.style, { position: 'fixed', inset: '0', pointerEvents: 'none', zIndex: '25' });
    document.body.appendChild(this.layer);

    this.linesLayer = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement;
    this.linesLayer.setAttribute('class', 'meeple-picker-lines');
    this.layer.appendChild(this.linesLayer);

    // The HUD's own top bar overlaps the canvas; without carving it out, a chip pushed upward by
    // collision resolution can land legible-but-ugly underneath it. Read, never touched — hud.ts stays
    // out of this lane's territory.
    this.topBarEl = document.querySelector('.hud-topbar');

    const canvas = scene.renderer.domElement;
    canvas.addEventListener('pointerdown', this.handlePointerDown);
    canvas.addEventListener('pointerup', this.handlePointerUp);
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointercancel', this.handlePointerCancel);

    this.untick = scene.onTick((dt) => this.tick(dt));
  }

  /** Same reasoning as `PlacementController.dispose()`: a picker that outlives its session keeps
   *  listening to the canvas and keeps its chip layer in the DOM. */
  dispose(): void {
    this.deactivate();
    const canvas = this.scene.renderer.domElement;
    canvas.removeEventListener('pointerdown', this.handlePointerDown);
    canvas.removeEventListener('pointerup', this.handlePointerUp);
    canvas.removeEventListener('pointermove', this.handlePointerMove);
    canvas.removeEventListener('pointercancel', this.handlePointerCancel);
    this.untick();
    this.layer.remove();
  }

  get isActive(): boolean {
    return this.active;
  }

  activate(): void {
    // Idempotent on purpose. `advance()` legitimately runs more than once for the same phase — the
    // session listener fires it, and so does the post-action callback — and without this every target
    // was rendered twice, stacking duplicate chips that then intercepted each other's clicks and made
    // the meeple unplaceable. Rebuild rather than append.
    if (this.entries.length > 0) this.deactivate();

    const state = this.session.state;
    if (state.phase !== 'awaiting_meeple' || !state.lastPlaced) return;
    const lastPlaced = state.lastPlaced;
    const tileGroup = this.board.getTileGroup(lastPlaced);
    const placed = state.board.get(posKey(lastPlaced));
    const def = placed ? this.session.tiles.get(placed.defId) : undefined;
    if (!tileGroup || !def) return;

    this.active = true;
    this.hoveredIndex = null;
    // Disarmed until the tap that placed the tile has finished echoing through the browser. See
    // `ARM_DELAY_MS`; the chips are also left `pointer-events: none` until then, so the ghost click
    // falls through to the canvas instead of being swallowed by a button nobody aimed at.
    this.armedAt = performance.now() + ARM_DELAY_MS;
    window.clearTimeout(this.armTimer);
    this.armTimer = window.setTimeout(() => {
      for (const entry of this.entries) entry.chip.style.pointerEvents = 'auto';
    }, ARM_DELAY_MS);
    // The played tile's footprint, in the tile's own frame. Transformed and projected every frame so
    // the lane tracks the camera AND the tile's placement animation, and so it is the TILE that is
    // kept clear rather than merely the chips kept apart.
    const half = TILE_SIZE / 2;
    this.tileGroup = tileGroup;
    this.tileCorners = [
      new Vector3(-half, TILE_HEIGHT, -half),
      new Vector3(half, TILE_HEIGHT, -half),
      new Vector3(half, TILE_HEIGHT, half),
      new Vector3(-half, TILE_HEIGHT, half),
    ];

    const targets = new Set(this.session.legalMeepleTargets());
    const seatName = (seat: number): string => this.seatName(seat);

    // Every feature of the tile gets a chip — the available ones in colour, the rest greyed with the
    // reason. See `unavailableReason`.
    def.features.forEach((feature, featureIndex) => {
      if (!feature) return;
      const available = targets.has(featureIndex);
      const reason = available
        ? null
        : unavailableReason(state, lastPlaced, featureIndex, seatName);
      // Legal per the engine but with no reason we can articulate: say nothing rather than guess.
      if (!available && !reason) return;

      const paletteIndex = [...targets].indexOf(featureIndex);
      const colour = available
        ? TARGET_PALETTE[(paletteIndex < 0 ? featureIndex : paletteIndex) % TARGET_PALETTE.length] ?? '#56b4e9'
        : TAKEN_COLOUR;
      const anchor = featureAnchor(def, featureIndex);

      const baseOpacity = available ? 0.85 : 0.4;
      const ringMesh = new Mesh(
        new RingGeometry(TILE_SIZE * 0.09, TILE_SIZE * 0.13, 24),
        new MeshBasicMaterial({ color: colour, transparent: true, opacity: baseOpacity, side: DoubleSide }),
      );
      ringMesh.rotation.x = -Math.PI / 2;

      // Invisible but raycast-hittable — bigger than the drawn ring so tapping near it on a phone
      // still lands. Opacity 0 (not `visible = false`): raycasting is geometry-based, not alpha-based.
      const hitMesh = new Mesh(
        new CircleGeometry(TILE_SIZE * HIT_RADIUS_SCALE, 20),
        new MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
      );
      hitMesh.rotation.x = -Math.PI / 2;
      hitMesh.userData['featureIndex'] = featureIndex;

      const ring = new Group();
      ring.add(ringMesh);
      ring.add(hitMesh);
      ring.position.set(anchor.x * TILE_SIZE, TILE_HEIGHT + 0.02, anchor.z * TILE_SIZE);
      tileGroup.add(ring);

      const info = describeFeature(state.forest, state.board, this.session.tiles, nodeId(lastPlaced, featureIndex));
      const points = info ? completedValue(info) : 0;
      const openPoints = info ? endValue(info, state.board) : 0;
      const complete = info?.complete ?? false;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = available ? 'meeple-chip' : 'meeple-chip meeple-chip--taken';
      chip.style.pointerEvents = 'none';
      chip.style.setProperty('--chip-colour', colour);
      if (!available) {
        // `aria-disabled` rather than `disabled`: a disabled button is unfocusable and stops firing
        // hover events in some browsers, which would kill the very ring-linkage that shows you WHERE
        // the claimed feature is. It is announced as unavailable and it refuses clicks.
        chip.setAttribute('aria-disabled', 'true');
      }

      const dot = document.createElement('span');
      dot.className = 'meeple-chip-dot';
      dot.setAttribute('aria-hidden', 'true');
      chip.appendChild(dot);

      const label = document.createElement('span');
      label.className = 'meeple-chip-label';
      label.textContent = available
        ? labelFor(feature.kind, points, openPoints, complete)
        : `${KIND_LABEL[feature.kind]} · ${reason}`;
      chip.appendChild(label);

      chip.addEventListener('click', () => this.choose(featureIndex));
      chip.addEventListener('mouseenter', () => this.setHovered(featureIndex));
      chip.addEventListener('mouseleave', () => {
        if (this.hoveredIndex === featureIndex) this.setHovered(null);
      });
      chip.addEventListener('focus', () => this.setHovered(featureIndex));
      chip.addEventListener('blur', () => {
        if (this.hoveredIndex === featureIndex) this.setHovered(null);
      });

      this.layer.appendChild(chip);
      // Measured before the first tick() sets `position: absolute` on it — button still has its
      // natural content-driven size from static-flow layout at this point.
      const chipSize = { w: chip.offsetWidth, h: chip.offsetHeight };

      // Shadow first: SVG paints in document order, so this must be appended before its line.
      const lineShadow = document.createElementNS('http://www.w3.org/2000/svg', 'line') as SVGLineElement;
      lineShadow.setAttribute('class', 'meeple-leader-line-shadow');
      this.linesLayer.appendChild(lineShadow);

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line') as SVGLineElement;
      line.setAttribute('class', 'meeple-leader-line');
      line.setAttribute('stroke', colour);
      this.linesLayer.appendChild(line);

      const localAnchor = new Vector3(anchor.x * TILE_SIZE, TILE_HEIGHT + 0.25, anchor.z * TILE_SIZE);

      this.entries.push({
        featureIndex,
        available,
        chip,
        ring,
        ringMesh,
        hitMesh,
        line,
        lineShadow,
        localAnchor,
        baseOpacity,
        screenAnchor: { x: 0, y: 0 },
        chipPos: { x: 0, y: 0 },
        chipSize,
        pulseFade: 0,
      });
    });

    this.tick(0);
  }

  deactivate(): void {
    this.active = false;
    this.hoveredIndex = null;
    this.pointerDown = null;
    window.clearTimeout(this.armTimer);
    this.armTimer = 0;
    for (const entry of this.entries) {
      entry.ring.parent?.remove(entry.ring);
      entry.ringMesh.geometry.dispose();
      entry.ringMesh.material.dispose();
      entry.hitMesh.geometry.dispose();
      entry.hitMesh.material.dispose();
      entry.chip.remove();
      entry.line.remove();
      entry.lineShadow.remove();
    }
    this.entries = [];
    this.tileGroup = null;
    this.tileCorners = [];
  }

  skip(): void {
    if (!this.active) return;
    const result = this.session.skipMeeple();
    this.deactivate();
    if (result.ok) this.onResolved();
  }

  private choose(featureIndex: number): void {
    if (!this.active) return;
    // The second half of the ghost-click guard, and the load-bearing one: `pointer-events` only stops
    // the DOM chips, while the on-board ring is chosen by raycasting a pointer event the CSS never
    // sees. One clock governs both, so neither path can claim a meeple the player has not looked at.
    if (performance.now() < this.armedAt) return;
    // A greyed chip is a label, not a button. It is still hoverable and focusable so it can light up
    // its ring and show you where the claimed feature runs to.
    if (!this.entries.find((e) => e.featureIndex === featureIndex)?.available) return;
    const result = this.session.placeMeeple(featureIndex);
    this.deactivate();
    if (result.ok) {
      this.sounds.play('meeple');
      this.onResolved();
    }
  }

  private setHovered(index: number | null): void {
    if (this.hoveredIndex === index) return;
    this.hoveredIndex = index;
    for (const entry of this.entries) {
      entry.chip.classList.toggle('meeple-chip--emphasis', entry.featureIndex === index);
    }
  }

  /**
   * The play area the HUD is not already sitting on.
   *
   * The HUD is a grid of opaque panels over the canvas, so "inside the viewport" was never the right
   * constraint — a chip clamped to the viewport can still land under the log or behind the tile panel.
   * Read from the live element boxes rather than from hardcoded numbers, because the layout reflows
   * (and on a narrow screen it reflows into something completely different).
   */
  private safeArea(rect: DOMRect): { left: number; right: number; top: number; bottom: number } {
    const box = (selector: string): DOMRect | null =>
      document.querySelector(selector)?.getBoundingClientRect() ?? null;

    let top = rect.top;
    const topbar = this.topBarEl?.getBoundingClientRect();
    if (topbar) top = Math.max(top, topbar.bottom);

    // The right-hand column (seats, mini-map, log) — take the leftmost edge any of them occupies.
    let right = rect.right;
    for (const selector of ['.hud-seats', '.hud-minimap', '.hud-log']) {
      const b = box(selector);
      if (b && b.width > 0 && b.left > rect.left + rect.width * 0.35) right = Math.min(right, b.left);
    }

    // The tile panel sits along the bottom.
    let bottom = rect.bottom;
    const tilePanel = box('.hud-tile-panel');
    if (tilePanel && tilePanel.height > 0) bottom = Math.min(bottom, tilePanel.top);

    return {
      left: rect.left + VIEWPORT_PADDING_PX,
      right: right - VIEWPORT_PADDING_PX,
      top: top + VIEWPORT_PADDING_PX,
      bottom: bottom - VIEWPORT_PADDING_PX,
    };
  }

  /**
   * Stack the chips in a lane beside the played tile — never over it. See `LANE_GAP_PX`.
   *
   * Order is by where each ring actually sits on screen, top to bottom, so the column reads in the
   * same order as the board and the leader lines cross as little as possible. The side is whichever
   * has more room, so a tile near the left edge pushes its chips right and vice versa. If the stack is
   * taller than the room available it tightens its gaps and then goes compact, because a chip you
   * cannot read is no better than a chip you cannot see.
   */
  private resolveChipPositions(rect: DOMRect): void {
    const safe = this.safeArea(rect);
    if (this.entries.length === 0) return;

    // The tile's screen box, and a fallback for the frame before it is known.
    let boxLeft = Infinity;
    let boxRight = -Infinity;
    let boxTop = Infinity;
    let boxBottom = -Infinity;
    for (const corner of this.tileCorners) {
      const world = corner.clone();
      if (this.tileGroup) this.tileGroup.localToWorld(world);
      const v = world.project(this.scene.camera);
      const x = rect.left + (v.x * 0.5 + 0.5) * rect.width;
      const y = rect.top + (-v.y * 0.5 + 0.5) * rect.height;
      boxLeft = Math.min(boxLeft, x);
      boxRight = Math.max(boxRight, x);
      boxTop = Math.min(boxTop, y);
      boxBottom = Math.max(boxBottom, y);
    }
    if (!Number.isFinite(boxLeft)) {
      boxLeft = boxRight = (safe.left + safe.right) / 2;
      boxTop = boxBottom = (safe.top + safe.bottom) / 2;
    }

    const order = [...this.entries].sort((a, b) => a.screenAnchor.y - b.screenAnchor.y);
    const widest = Math.max(...order.map((e) => e.chipSize.w));
    const roomLeft = boxLeft - LANE_GAP_PX - safe.left;
    const roomRight = safe.right - (boxRight + LANE_GAP_PX);
    // Prefer the side that can hold a whole chip; if neither can, take the roomier one and let the
    // clamp below do what it can — better a chip half over the tile than a chip off screen.
    const onRight = roomRight >= widest || roomRight >= roomLeft;
    const laneX = onRight ? boxRight + LANE_GAP_PX + widest / 2 : boxLeft - LANE_GAP_PX - widest / 2;

    const totalHeight = order.reduce((sum, e) => sum + e.chipSize.h, 0);
    const available = safe.bottom - safe.top;
    const naturalGap = CHIP_GAP_PX;
    const gap =
      totalHeight + naturalGap * (order.length - 1) <= available
        ? naturalGap
        : Math.max(CHIP_MIN_GAP_PX, (available - totalHeight) / Math.max(1, order.length - 1));
    const stackHeight = totalHeight + gap * (order.length - 1);

    // Centred on the tile, then slid wholesale into the safe area — moving the block keeps the reading
    // order intact, where clamping each chip individually would pile them up on the edge.
    let y = (boxTop + boxBottom) / 2 - stackHeight / 2;
    y = clamp(y, safe.top, Math.max(safe.top, safe.bottom - stackHeight));

    for (const entry of order) {
      const halfW = entry.chipSize.w / 2;
      entry.chipPos = {
        x: clamp(laneX, safe.left + halfW, Math.max(safe.left + halfW, safe.right - halfW)),
        y: y + entry.chipSize.h / 2,
      };
      y += entry.chipSize.h + gap;
    }
  }

  private tick(dt: number): void {
    if (!this.active) return;
    const canvas = this.scene.renderer.domElement;
    const rect = canvas.getBoundingClientRect();

    const group = this.tileGroup;
    for (const entry of this.entries) {
      const world = entry.localAnchor.clone();
      if (group) group.localToWorld(world);
      const v = world.project(this.scene.camera);
      entry.screenAnchor = {
        x: rect.left + (v.x * 0.5 + 0.5) * rect.width,
        y: rect.top + (-v.y * 0.5 + 0.5) * rect.height,
      };
    }

    this.resolveChipPositions(rect);
    this.pulseClock += dt;

    for (const entry of this.entries) {
      const emphasized = entry.featureIndex === this.hoveredIndex;

      entry.chip.style.position = 'absolute';
      entry.chip.style.left = `${entry.chipPos.x}px`;
      entry.chip.style.top = `${entry.chipPos.y}px`;
      entry.chip.style.transform = emphasized
        ? 'translate(-50%, calc(-50% - 6px)) scale(1.08)'
        : 'translate(-50%, -50%)';
      entry.chip.style.zIndex = emphasized ? '2' : '1';

      for (const l of [entry.lineShadow, entry.line]) {
        l.setAttribute('x1', String(entry.screenAnchor.x));
        l.setAttribute('y1', String(entry.screenAnchor.y));
        l.setAttribute('x2', String(entry.chipPos.x));
        l.setAttribute('y2', String(entry.chipPos.y));
      }

      // Reduced motion: swap the loop for a static emphasis, applied instantly (no eased ramp — the
      // point is to remove motion, not just shrink it). Otherwise: a continuous throb whose amplitude
      // eases in on hover/focus and back out to zero when it ends, per the brief.
      if (this.reducedMotion) {
        entry.pulseFade = emphasized ? 1 : 0;
        entry.ringMesh.scale.setScalar(1 + entry.pulseFade * 0.22);
        entry.ringMesh.material.opacity = entry.baseOpacity + entry.pulseFade * (1 - entry.baseOpacity);
      } else {
        const damp = Math.min(1, 1 - Math.pow(0.001, dt));
        entry.pulseFade += ((emphasized ? 1 : 0) - entry.pulseFade) * damp;
        const throb = Math.sin(this.pulseClock * PULSE_HZ * Math.PI * 2) * 0.5 + 0.5;
        entry.ringMesh.scale.setScalar(1 + throb * 0.22 * entry.pulseFade);
        entry.ringMesh.material.opacity = entry.baseOpacity + throb * 0.35 * entry.pulseFade;
      }
    }
  }

  private ndcFromEvent(event: PointerEvent): Vector2 {
    const rect = this.scene.renderer.domElement.getBoundingClientRect();
    return new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
  }

  /** Raycasts against every ring's (generous, invisible) hit-area and returns the feature it belongs to. */
  private pickRing(event: PointerEvent): number | null {
    if (this.entries.length === 0) return null;
    this.raycaster.setFromCamera(this.ndcFromEvent(event), this.scene.camera);
    const hits = this.raycaster.intersectObjects(this.entries.map((e) => e.hitMesh));
    const hit = hits[0];
    if (!hit) return null;
    const featureIndex = hit.object.userData['featureIndex'];
    return typeof featureIndex === 'number' ? featureIndex : null;
  }

  private handlePointerDown = (event: PointerEvent): void => {
    if (!this.active) return;
    this.pointerDown = { x: event.clientX, y: event.clientY };
  };

  private handlePointerUp = (event: PointerEvent): void => {
    if (!this.active) return;
    const down = this.pointerDown;
    this.pointerDown = null;
    if (!down) return;
    const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
    if (moved > CLICK_SLOP_PX) return; // a camera drag, not a tap
    const hit = this.pickRing(event);
    if (hit !== null) this.choose(hit);
  };

  private handlePointerMove = (event: PointerEvent): void => {
    if (!this.active) return;
    this.setHovered(this.pickRing(event));
  };

  private handlePointerCancel = (): void => {
    this.pointerDown = null;
  };
}
